import QRCodeStyling from 'qr-code-styling'

import {
	TRANSFORMATION_STATUS,
	TStorageProvider,
	TUploadedMedia,
} from '@/services'

import {
	ensureQrVisibility,
	fixIncompatibleLegacyQrDesign,
} from '../qrImageBuilder'
import { TQrDesign } from '../qrImageTypes'
import { canLoadImage } from './canLoadImage'

const VALID_IMAGE_MIME_TYPE = /^image\/[\w.+-]+$/

const isSmallTransformationApplied = (
	transformationStatus?: TRANSFORMATION_STATUS,
) => transformationStatus === TRANSFORMATION_STATUS.transformedFfmpeg

const getUploadedImageUrl = (image: TUploadedMedia) =>
	`${image.bucketRootUrl}/${image.publicId}${isSmallTransformationApplied(image.hasAutoTransformation) ? '/small' : ''}`

const fetchAndProcessImage = async (
	imageURL: string,
	storageProvider?: TStorageProvider,
): Promise<string> => {
	try {
		const response = await fetch(imageURL, {
			cache: 'no-cache',
			mode: storageProvider === 'r2' ? undefined : 'cors',
		})

		if (response.status >= 400) {
			throw new Error('Failed to fetch image', {
				cause: response.statusText,
			})
		}

		const blob = await response.blob()
		const dataURL = await new Promise<string>((resolve) => {
			const reader = new FileReader()
			reader.onloadend = () => resolve(reader.result as string)
			reader.onerror = () => resolve('')
			reader.onabort = () => resolve('')
			reader.readAsDataURL(blob)
		})

		const mimeType = dataURL.slice('data:'.length, dataURL.indexOf(';'))
		if (!VALID_IMAGE_MIME_TYPE.test(mimeType)) {
			const imageBase64 = dataURL.split(',')[1]
			const imageString = atob(imageBase64 || '')
			if (imageString.includes('<svg')) {
				return `data:image/svg+xml;base64,${imageBase64}`
			}
		}

		return dataURL
	} catch (error) {
		console.error('[fetchAndProcessImage] Error fetching image:', error)
		return '' // Return empty string if fetch fails
	}
}

const fileToDataUrl = (file: File): Promise<string> =>
	new Promise<string>((resolve) => {
		const reader = new FileReader()
		reader.onloadend = () => resolve(reader.result as string)
		reader.onerror = () => resolve('')
		reader.onabort = () => resolve('')
		reader.readAsDataURL(file)
	})

export const MAX_LOGO_IMAGE_SIZE = 0.4
const MAX_LOGO_AXIS_RATIO = 1 / 3
const ERROR_CORRECTION_PERCENTS: Record<string, number> = {
	L: 0.07,
	M: 0.15,
	Q: 0.25,
	H: 0.3,
}

const loadImageAspectRatio = (src: string) =>
	new Promise<number | null>((resolve) => {
		const image = new Image()
		image.onload = () =>
			resolve(
				image.naturalWidth > 0 && image.naturalHeight > 0
					? image.naturalHeight / image.naturalWidth
					: null,
			)
		image.onerror = () => resolve(null)
		image.src = src
	})

const toOddFloor = (value: number) => {
	const floored = Math.max(1, Math.floor(value))
	return floored % 2 === 0 ? floored - 1 : floored
}

const getLogoImageSize = ({
	moduleCount,
	errorCorrectionPercent,
	aspectRatio,
}: {
	moduleCount: number
	errorCorrectionPercent: number
	aspectRatio: number
}) => {
	const maxAxisModules = toOddFloor(moduleCount * MAX_LOGO_AXIS_RATIO)
	const maxWidthModules = toOddFloor(
		Math.min(maxAxisModules, maxAxisModules / aspectRatio),
	)

	for (
		let widthModules = maxWidthModules;
		widthModules >= 1;
		widthModules -= 2
	) {
		const heightModules =
			1 + 2 * Math.ceil((widthModules * aspectRatio - 1) / 2)
		const maxHiddenModules =
			Math.ceil((widthModules + 2) ** 2 * aspectRatio) - 1

		if (
			heightModules <= maxAxisModules &&
			maxHiddenModules >= widthModules * heightModules
		) {
			const imageSize =
				(maxHiddenModules + 0.5) /
				(errorCorrectionPercent * moduleCount ** 2)
			return Math.min(MAX_LOGO_IMAGE_SIZE, imageSize)
		}
	}

	return MAX_LOGO_IMAGE_SIZE
}

export const createQrPatternInstance = async (
	_qrDesign: TQrDesign,
	logoFile?: File | null,
) => {
	const qrDesign = fixIncompatibleLegacyQrDesign(ensureQrVisibility(_qrDesign))
	const { image, ...designWithoutImage } = qrDesign
	const isSelectedImage = image?.selected
	const imageURL =
		(isSelectedImage && image?.selected) ||
		(image?.uploaded && getUploadedImageUrl(image.uploaded))

	const imageDataUrl = imageURL
		? await fetchAndProcessImage(imageURL, image?.uploaded?.storageProvider)
		: logoFile
		? await fileToDataUrl(logoFile)
		: ''

	const isImageLoadable = !!imageDataUrl && (await canLoadImage(imageDataUrl))
	if (imageDataUrl && !isImageLoadable) {
		console.error(
			'[createQrPatternInstance] Logo cannot be decoded, rendering QR without it',
		)
	}
	const isLogoDropped = (!!imageURL || !!logoFile) && !isImageLoadable

	const qrPattern = new QRCodeStyling({
		...designWithoutImage,
		image: isImageLoadable ? imageDataUrl : '',
		// Ignores legacy width and height from qrDesign and forces them to 320px to avoid unwanted padding around the pattern.
		// This works with the current length of encoded QR landing URLs and is directly related to the encoded data length.
		// It would be nice in the future to define a formula that would calculate the optimal width and height based on the data length.
		width: 320,
		height: 320,
		margin: 0,
		imageOptions: {
			hideBackgroundDots: true,
			margin: 0,
			imageSize: MAX_LOGO_IMAGE_SIZE,
		},
	})

	const result = { qrPattern, isLogoDropped }
	if (!isImageLoadable) {
		return result
	}

	const moduleCount = qrPattern._qr?.getModuleCount()
	if (!moduleCount) {
		return result
	}

	const errorCorrectionPercent =
		ERROR_CORRECTION_PERCENTS[
			qrPattern._options.qrOptions.errorCorrectionLevel
		]
	const aspectRatio = await loadImageAspectRatio(imageDataUrl)

	if (!errorCorrectionPercent || !aspectRatio) {
		return result
	}

	const maxImageSize = Math.min(
		MAX_LOGO_IMAGE_SIZE,
		qrDesign.imageOptions?.imageSize || MAX_LOGO_IMAGE_SIZE,
	)
	const imageSize = Math.min(
		maxImageSize,
		getLogoImageSize({
			moduleCount,
			errorCorrectionPercent,
			aspectRatio,
		}),
	)

	if (imageSize < MAX_LOGO_IMAGE_SIZE) {
		qrPattern.update({
			imageOptions: { ...qrPattern._options.imageOptions, imageSize },
		})
	}

	return result
}
